import { FormEvent, useState } from "react";
import dayjs from "dayjs";
import type { AdminContentRoyalty, AdminCreatorRoyalty } from "../api/adminRoyaltiesApi";
import { searchContentRoyalties, searchCreatorRoyalties } from "../api/adminRoyaltiesApi";

type Mode = "creators" | "contents";

export default function AdminRoyalties() {
  const [mode, setMode] = useState<Mode>("creators");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [creatorResults, setCreatorResults] = useState<AdminCreatorRoyalty[]>([]);
  const [contentResults, setContentResults] = useState<AdminContentRoyalty[]>([]);

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (mode === "creators") {
        const data = await searchCreatorRoyalties(query, 5);
        setCreatorResults(data);
      } else {
        const data = await searchContentRoyalties(query, 8);
        setContentResults(data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="rounded-3xl bg-slate-900/70 p-6 shadow-lg shadow-black/20">
        <p className="text-sm uppercase tracking-wider text-slate-400">Royalty &amp; Payout Dashboard</p>
        <h1 className="text-2xl font-semibold text-white">30 Günlük Ödeme Döngüsü</h1>
        <p className="text-sm text-slate-300">Creator veya seri bazında bekleyen ödemeleri inceleyin.</p>
      </header>

      <section className="rounded-3xl border border-white/10 bg-slate-900/60 p-6">
        <form onSubmit={handleSearch} className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex gap-2 rounded-full border border-white/10 p-1 text-xs font-semibold text-slate-200">
            <button
              type="button"
              className={`flex-1 rounded-full px-4 py-2 ${mode === "creators" ? "bg-emerald-500 text-slate-900" : "bg-transparent text-slate-200"}`}
              onClick={() => setMode("creators")}
            >
              Creator araması
            </button>
            <button
              type="button"
              className={`flex-1 rounded-full px-4 py-2 ${mode === "contents" ? "bg-emerald-500 text-slate-900" : "bg-transparent text-slate-200"}`}
              onClick={() => setMode("contents")}
            >
              Seri araması
            </button>
          </div>
          <input
            type="text"
            className="flex-1 rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-white outline-none focus:border-emerald-500"
            placeholder={mode === "creators" ? "Creator adı veya e-posta" : "Seri adı"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="submit"
            className="rounded-2xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "Aranıyor..." : "Ara"}
          </button>
        </form>
      </section>

      {mode === "creators" ? (
        <CreatorResultTable results={creatorResults} />
      ) : (
        <ContentResultTable results={contentResults} />
      )}
    </div>
  );
}

function CreatorResultTable({ results }: { results: AdminCreatorRoyalty[] }) {
  if (results.length === 0) {
    return (
      <section className="rounded-3xl border border-white/5 bg-slate-950/50 p-6 text-sm text-slate-400">
        Henüz creator araması yapılmadı veya eşleşen kayıt bulunamadı.
      </section>
    );
  }

  return (
    <section className="overflow-x-auto rounded-3xl border border-white/5 bg-slate-950/70 p-4 text-xs text-slate-200">
      <table className="min-w-full divide-y divide-white/10">
        <thead className="text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Creator</th>
            <th className="px-3 py-2 text-left">Dönem</th>
            <th className="px-3 py-2 text-right">ZTN</th>
            <th className="px-3 py-2 text-right">TRY</th>
            <th className="px-3 py-2 text-right">Kalan Gün</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {results.map((creator) => (
            <tr key={creator.creatorId}>
              <td className="px-3 py-3">
                <div className="font-semibold text-white">{creator.creatorName}</div>
                <div className="text-[11px] text-slate-400">{creator.creatorEmail ?? "—"}</div>
              </td>
              <td className="px-3 py-3 text-slate-300">
                {dayjs(creator.cycleStartUtc).format("DD MMM")} - {dayjs(creator.cycleEndUtc).format("DD MMM")}
              </td>
              <td className="px-3 py-3 text-right text-emerald-300">{creator.shareTokenAmount.toFixed(2)} ZTN</td>
              <td className="px-3 py-3 text-right">
                {creator.currencyAmount.toFixed(2)} {creator.currencyCode}
              </td>
              <td className="px-3 py-3 text-right">
                <span className={creator.daysRemaining <= 3 ? "text-amber-300" : "text-slate-200"}>
                  {creator.daysRemaining} gün
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ContentResultTable({ results }: { results: AdminContentRoyalty[] }) {
  if (results.length === 0) {
    return (
      <section className="rounded-3xl border border-white/5 bg-slate-950/50 p-6 text-sm text-slate-400">
        Henüz seri araması yapılmadı veya eşleşen kayıt bulunamadı.
      </section>
    );
  }

  return (
    <section className="overflow-x-auto rounded-3xl border border-white/5 bg-slate-950/70 p-4 text-xs text-slate-200">
      <table className="min-w-full divide-y divide-white/10">
        <thead className="text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Seri</th>
            <th className="px-3 py-2 text-right">Premium</th>
            <th className="px-3 py-2 text-right">Ücretsiz</th>
            <th className="px-3 py-2 text-right">Tekli</th>
            <th className="px-3 py-2 text-right">ZTN</th>
            <th className="px-3 py-2 text-right">TRY</th>
            <th className="px-3 py-2 text-right">Kalan Gün</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {results.map((content) => (
            <tr key={content.contentId} className={content.daysRemaining !== undefined && content.daysRemaining <= 3 ? "bg-amber-500/10" : ""}>
              <td className="px-3 py-3">
                <div className="font-semibold text-white">{content.title}</div>
                <div className="text-[11px] text-slate-400">
                  {content.cycleStartUtc && content.cycleEndUtc
                    ? `${dayjs(content.cycleStartUtc).format("DD MMM")} - ${dayjs(content.cycleEndUtc).format("DD MMM")}`
                    : "Aktif döngü"}
                </div>
              </td>
              <td className="px-3 py-3 text-right">{content.premiumReadCount}</td>
              <td className="px-3 py-3 text-right">{content.freeReadCount}</td>
              <td className="px-3 py-3 text-right">{content.singleUnlockCount}</td>
              <td className="px-3 py-3 text-right">{content.shareTokenAmount.toFixed(2)}</td>
              <td className="px-3 py-3 text-right">
                {content.currencyAmount.toFixed(2)} {content.currencyCode}
              </td>
              <td className="px-3 py-3 text-right">
                <span className={content.daysRemaining !== undefined && content.daysRemaining <= 3 ? "text-amber-300" : "text-slate-200"}>
                  {content.daysRemaining ?? "-"} gün
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
